import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { updateQuoteOutcome } from "../../../services/apiService";
import { modelLabel, money, shortDate, tierLabel } from "./trialFormat";

/**
 * Record what happened to one trial quote — and, once the job is built, what
 * it actually took. Nothing is filled in for the user: a blank stays blank.
 */

function formFromRow(row) {
  return {
    quote_status: row?.status || "open",
    quote_decision_date: row?.decision_date || "",
    quote_accepted_price: row?.accepted_price || "",
    quote_discount_amount: row?.discount || "",
    quote_loss_reason: row?.loss_reason || "",
    actual_material_cost: row?.actual_material_cost || "",
    actual_shop_hours: row?.actual_shop_hours || "",
    actual_notes: row?.actual_notes || "",
  };
}

function Field({ id, label, hint, children }) {
  return (
    <div className="trial-form__field">
      <label htmlFor={id}>{label}</label>
      {children}
      {hint ? <p className="trial-hint">{hint}</p> : null}
    </div>
  );
}

function QuoteOutcomeDialog({ row, onClose, onSaved }) {
  const open = Boolean(row);
  const [form, setForm] = useState(() => formFromRow(row));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setForm(formFromRow(row));
    setError(null);
    setSaving(false);
  }, [row]);

  const set = (field) => (event) =>
    setForm((current) => ({ ...current, [field]: event.target.value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!row || saving) return;
    setSaving(true);
    setError(null);
    try {
      // Send only what this person changed: the row may be stale, and a
      // full snapshot would overwrite someone else's newer outcome.
      const initial = formFromRow(row);
      const changes = Object.fromEntries(
        Object.entries(form).filter(
          ([field, value]) => value !== initial[field],
        ),
      );
      if (Object.keys(changes).length === 0) {
        setSaving(false);
        onClose?.();
        return;
      }
      const saved = await updateQuoteOutcome(row.job_id, changes);
      onSaved?.(saved);
    } catch (err) {
      setError(err?.error || err?.message || "Could not save. Try again.");
      setSaving(false);
    }
  };

  const decided = form.quote_status !== "open";

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !saving) onClose?.();
      }}
    >
      <DialogContent className="trial-dialog">
        <DialogHeader>
          <DialogTitle>{row?.job_name || "Trial quote"}</DialogTitle>
          <DialogDescription>
            {row
              ? `${row.customer_name || "No customer on file"} · ${tierLabel(
                  row.tier,
                )} · quoted ${shortDate(
                  row.created_at,
                )} · rafter tails ${money(row.quoted_before_tax)} before tax${
                  row.job_has_other_items
                    ? " (other items on the job not included)"
                    : ""
                } · ${modelLabel(row.model_version)}`
              : null}
          </DialogDescription>
        </DialogHeader>

        <form className="trial-form" onSubmit={handleSubmit} noValidate>
          <div className="trial-form__grid">
            <Field id="trial-status" label="Result">
              <select
                id="trial-status"
                className="wb-input"
                value={form.quote_status}
                onChange={set("quote_status")}
              >
                <option value="open">Still open</option>
                <option value="won">Won</option>
                <option value="lost">Lost</option>
              </select>
            </Field>
            <Field
              id="trial-decision-date"
              label="Decided on"
              hint={decided ? null : "Fill in once the customer decides."}
            >
              <input
                id="trial-decision-date"
                type="date"
                className="wb-input"
                value={form.quote_decision_date}
                onChange={set("quote_decision_date")}
              />
            </Field>
            <Field
              id="trial-accepted"
              label="Accepted price for the rafter tails ($)"
              hint="If won — before tax, without delivery or other lines."
            >
              <input
                id="trial-accepted"
                inputMode="decimal"
                className="wb-input"
                value={form.quote_accepted_price}
                onChange={set("quote_accepted_price")}
              />
            </Field>
            <Field
              id="trial-discount"
              label="Discount given ($)"
              hint="Any price cut off the quote."
            >
              <input
                id="trial-discount"
                inputMode="decimal"
                className="wb-input"
                value={form.quote_discount_amount}
                onChange={set("quote_discount_amount")}
              />
            </Field>
          </div>

          {form.quote_status === "lost" ? (
            <Field
              id="trial-loss-reason"
              label="Why we lost it"
              hint="Only what the customer actually said. Leave blank if they didn't say."
            >
              <textarea
                id="trial-loss-reason"
                className="wb-input trial-form__text"
                rows={2}
                value={form.quote_loss_reason}
                onChange={set("quote_loss_reason")}
              />
            </Field>
          ) : null}

          <fieldset className="trial-form__section">
            <legend>After the job is built</legend>
            <div className="trial-form__grid">
              <Field id="trial-material" label="Actual material cost ($)">
                <input
                  id="trial-material"
                  inputMode="decimal"
                  className="wb-input"
                  value={form.actual_material_cost}
                  onChange={set("actual_material_cost")}
                />
              </Field>
              <Field id="trial-hours" label="Actual shop hours">
                <input
                  id="trial-hours"
                  inputMode="decimal"
                  className="wb-input"
                  value={form.actual_shop_hours}
                  onChange={set("actual_shop_hours")}
                />
              </Field>
            </div>
            <Field id="trial-notes" label="Notes">
              <textarea
                id="trial-notes"
                className="wb-input trial-form__text"
                rows={2}
                value={form.actual_notes}
                onChange={set("actual_notes")}
              />
            </Field>
          </fieldset>

          {error ? (
            <p className="trial-form__error" role="alert">
              {error}
            </p>
          ) : null}

          <div className="trial-form__actions">
            <button
              type="button"
              className="wb-tbtn wb-tbtn--edge"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="wb-tbtn wb-tbtn--solid"
              disabled={saving}
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default QuoteOutcomeDialog;
